/**
 * Online-only workforce, attendance and payroll endpoints.
 *
 * Nothing here is ever queued: rates, payout destinations, day adjustments
 * and approvals, and every payroll step need a live, server-confirmed request
 * (queuing them would infer consent). Payroll approval, payment and payout
 * changes may also answer `STEP_UP_REQUIRED` — see `@/lib/auth/step-up`.
 *
 * Offline-capable writes (engage a worker, check in/out, correct) go through
 * the repositories in `@/lib/db` instead.
 */
import { api } from './client';
import {
  daySheetSchema,
  engagementSchema,
  headcountSchema,
  myWorkSchema,
  payrollRunSchema,
  tradeSchema,
  workerDaySchema,
  type DaySheet,
  type Engagement,
  type Headcount,
  type MyWork,
  type PayoutMethod,
  type PayrollRun,
  type Trade,
  type WorkerDay,
} from './workforce-types';

const base = (projectId: string) => `/projects/${projectId}`;

function parseList<T>(rows: unknown, parse: (row: unknown) => T | null): T[] {
  return (Array.isArray(rows) ? rows : []).flatMap((row) => {
    const parsed = parse(row);
    return parsed ? [parsed] : [];
  });
}

// -- workforce ---------------------------------------------------------------

export async function fetchTrades(): Promise<Trade[]> {
  const response = await api.get<unknown[]>('/projects/trades');
  return parseList(response.data, (row) => tradeSchema.safeParse(row).data ?? null);
}

/** The roster with `current_rate` (only for cost.view_internal). */
export async function fetchWorkers(projectId: string): Promise<Engagement[]> {
  const response = await api.get<unknown[]>(`${base(projectId)}/workers`);
  return parseList(response.data, (row) => engagementSchema.safeParse(row).data ?? null);
}

export type RateInput = {
  basis: string;
  rate_minor: number;
  effective_from: string;
  overtime_multiplier: number;
  full_day_hours: number;
};

export async function setLabourRate(
  projectId: string,
  engagementId: string,
  input: RateInput,
  idempotencyKey: string,
): Promise<Engagement> {
  const response = await api.post<unknown>(
    `${base(projectId)}/workers/${engagementId}/rates`,
    input,
    { idempotencyKey },
  );
  return engagementSchema.parse(response.data);
}

/** Step-up protected: may fail with STEP_UP_REQUIRED. */
export async function setPayoutDestination(
  projectId: string,
  engagementId: string,
  input: { method: PayoutMethod; destination: string },
  idempotencyKey: string,
): Promise<Engagement> {
  const response = await api.put<unknown>(
    `${base(projectId)}/workers/${engagementId}/payout`,
    input,
    { idempotencyKey },
  );
  return engagementSchema.parse(response.data);
}

// -- attendance --------------------------------------------------------------

export async function fetchDaySheet(projectId: string, workDate: string): Promise<DaySheet> {
  const response = await api.get<unknown>(`${base(projectId)}/attendance`, {
    query: { work_date: workDate },
  });
  return daySheetSchema.parse(response.data);
}

/** Who is on site per company and day — counts only (the main contractor's gate). */
export async function fetchHeadcount(
  projectId: string,
  start: string,
  end?: string,
): Promise<Headcount> {
  const response = await api.get<unknown>(`${base(projectId)}/attendance/headcount`, {
    query: { start, ...(end ? { end } : {}) },
  });
  return headcountSchema.parse(response.data);
}

export type DayAdjustment = { fraction?: number; overtime_hours?: number; reason: string };

export async function adjustWorkerDay(
  projectId: string,
  dayId: string,
  input: DayAdjustment,
  idempotencyKey: string,
): Promise<WorkerDay> {
  const response = await api.post<unknown>(
    `${base(projectId)}/attendance/days/${dayId}/adjust`,
    input,
    { idempotencyKey },
  );
  return workerDaySchema.parse(response.data);
}

/** 422 with `details.unresolved_days` while missing check-outs are unresolved. */
export async function approveAttendanceDay(
  projectId: string,
  workDate: string,
  idempotencyKey: string,
): Promise<{ approved: number; days: WorkerDay[] }> {
  const response = await api.post<{ approved: number; days: unknown[] }>(
    `${base(projectId)}/attendance/approve`,
    { work_date: workDate },
    { idempotencyKey },
  );
  return {
    approved: response.data.approved,
    days: parseList(response.data.days, (row) => workerDaySchema.safeParse(row).data ?? null),
  };
}

// -- payroll -----------------------------------------------------------------

export async function fetchPayrollRuns(projectId: string): Promise<PayrollRun[]> {
  const response = await api.get<unknown[]>(`${base(projectId)}/payroll`);
  return parseList(response.data, (row) => payrollRunSchema.safeParse(row).data ?? null);
}

export async function fetchPayrollRun(projectId: string, runId: string): Promise<PayrollRun> {
  const response = await api.get<unknown>(`${base(projectId)}/payroll/${runId}`);
  return payrollRunSchema.parse(response.data);
}

export async function createPayrollRun(
  projectId: string,
  period: { period_start: string; period_end: string },
  idempotencyKey: string,
): Promise<PayrollRun> {
  const response = await api.post<unknown>(`${base(projectId)}/payroll`, period, {
    idempotencyKey,
  });
  return payrollRunSchema.parse(response.data);
}

export type RunAction = 'recalculate' | 'submit' | 'review' | 'approve' | 'pay';

/** One workflow step. `approve` and `pay` are step-up protected. */
export async function payrollAction(
  projectId: string,
  runId: string,
  action: RunAction,
  idempotencyKey: string,
): Promise<PayrollRun> {
  const response = await api.post<unknown>(
    `${base(projectId)}/payroll/${runId}/${action}`,
    {},
    { idempotencyKey },
  );
  return payrollRunSchema.parse(response.data);
}

export async function addPayrollAdjustment(
  projectId: string,
  runId: string,
  input: { engagement_id: string; kind: string; amount_minor: number; reason: string },
  idempotencyKey: string,
): Promise<PayrollRun> {
  const response = await api.post<unknown>(
    `${base(projectId)}/payroll/${runId}/adjustments`,
    input,
    { idempotencyKey },
  );
  return payrollRunSchema.parse(response.data);
}

export async function cancelPayrollRun(
  projectId: string,
  runId: string,
  reason: string,
  idempotencyKey: string,
): Promise<PayrollRun> {
  const response = await api.post<unknown>(
    `${base(projectId)}/payroll/${runId}/cancel`,
    { reason },
    { idempotencyKey },
  );
  return payrollRunSchema.parse(response.data);
}

// -- the worker's own record (identity-only auth) ----------------------------

/** Link temporary profiles created for this person's verified phone number. */
export async function claimWorkerProfile(): Promise<{ worker_profile_id: string | null }> {
  const response = await api.post<{ worker_profile_id: string | null }>(
    '/me/worker-profile/claim',
    {},
  );
  return response.data;
}

export async function fetchMyWork(): Promise<MyWork> {
  const response = await api.get<unknown>('/me/worker-profile');
  return myWorkSchema.parse(response.data);
}
